import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { TransactionDetailsModal } from "./Modal/TransactionDetailsModal";
import { Transaction } from "./types";
import { useAppDispatch } from "../../hooks/hooks";
import { Currency } from "../../features/auth/store/types/currency-types";
import { fetchCurrencies } from "../../features/auth/store/currencySlice";

const formatDate = (timestamp?: string) => {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp);
  return date.toLocaleDateString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "2-digit",
  });
};

const formatTime = (timestamp?: string) => {
  if (!timestamp) return "N/A";
  const date = new Date(timestamp);
  return date.toLocaleTimeString("en-US", {
    hour: "2-digit",
    minute: "2-digit",
    hour12: true,
  });
};

const formatAmountWithSymbol = (
  amount: string | number | undefined,
  symbol: string,
) => {
  if (amount === undefined || amount === null || amount === "") return "-";

  const numericAmount =
    typeof amount === "string" ? parseFloat(amount) : amount;

  if (isNaN(numericAmount)) return "-";

  const formattedNumber = new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(numericAmount);

  if (!symbol) return formattedNumber;

  const normalized = symbol.toLowerCase();
  const spacer = /^[A-Za-z]+$/.test(symbol) ? " " : "";
  if (
    normalized === "coins" ||
    normalized === "point" ||
    normalized === "points"
  ) {
    return `${formattedNumber}${spacer}${symbol}`;
  }

  return `${symbol}${spacer}${formattedNumber}`;
};

const getStatusText = (purchase: Transaction) =>
  purchase.status_text || purchase.status || "Unknown";

const getItemsText = (purchase: Transaction) =>
  purchase.items ?? purchase.type_text ?? purchase.type ?? "-";

const statusBgStyles: Record<string, string> = {
  Pending: "bg-orange-100",
  Successful: "bg-green-100",
  Reversed: "bg-blue-100",
  Rejected: "bg-red-100",
  PENDING: "bg-orange-100",
  SUCCESSFUL: "bg-green-100",
  PAID: "bg-green-100",
  REVERSED: "bg-blue-100",
  FAILED: "bg-red-100",
  PROCESSING: "bg-yellow-100",
  QUEUED: "bg-yellow-200",
};

const StatusBadge = ({ statusText }: { statusText?: string }) => {
  const statusStyles: Record<string, string> = {
    Pending: "text-orange-500",
    Successful: "text-green-500",
    Reversed: "text-blue-500",
    Rejected: "text-red-500",
    PENDING: "text-orange-500",
    SUCCESSFUL: "text-green-500",
    PAID: "text-green-500",
    REVERSED: "text-blue-500",
    FAILED: "text-red-500",
    PROCESSING: "text-yellow-500",
    QUEUED: "text-yellow-600",
  };

  return (
    <span
      className={`px-2 py-1 rounded text-sm font-medium ${
        statusText ? statusStyles[statusText] : "text-gray-500"
      }`}
    >
      {statusText || "Unknown"}
    </span>
  );
};

export const PurchaseTable = ({
  purchases,
  loading = false,
}: {
  purchases: Transaction[];
  loading?: boolean;
}) => {
  const dispatch = useAppDispatch();
  const [selectedTransaction, setSelectedTransaction] =
    useState<Transaction | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const currencies: Currency[] = useSelector(
    (state: any) => state.currencies?.items ?? [],
  );

  const getCurrencySymbol = (currencyId?: number) => {
    const match = currencies.find((currency: Currency) => currency.id === currencyId);
    return match?.symbol || "";
  };

  const formatAmount = (purchase: Transaction) =>
    formatAmountWithSymbol(
      purchase.price ?? purchase.amount,
      getCurrencySymbol(purchase.currency_id) ||
        purchase.currency_symbol ||
        purchase.currency?.symbol ||
        "",
    );

  useEffect(() => {
    if (!currencies || currencies.length === 0) {
      dispatch(fetchCurrencies({ perPage: 200 }));
    }
  }, [dispatch, currencies]);

  const handleRowClick = (purchase: Transaction) => {
    const normalizedPurchase = {
      ...purchase,
      date: purchase.date || formatDate(purchase.created_at),
      time: purchase.time || formatTime(purchase.created_at),
      status: getStatusText(purchase),
      amount: formatAmount(purchase),
      subtotal: purchase.subtotal ?? purchase.price ?? purchase.amount,
      total: purchase.total ?? purchase.price ?? purchase.amount,
      fee: purchase.fee ?? "0",
      items_details:
        purchase.items_details && purchase.items_details.length > 0
          ? purchase.items_details
          : [
              {
                name:
                  purchase.description ||
                  purchase.type_text ||
                  purchase.type ||
                  "Transaction Item",
                username: "",
                image: "",
                currency_amount: "",
                price: formatAmount(purchase),
              },
            ],
    };

    setSelectedTransaction(normalizedPurchase);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (!purchases || purchases.length === 0) {
    return (
      <div className="flex items-center justify-center h-64 border border-[#FFFFFF1A] rounded-2xl">
        <div className="text-center">
          <p className="text-gray-500 dark:text-gray-400 text-lg mb-2">
            No transactions found
          </p>
          <p className="text-gray-400 dark:text-gray-500 text-sm">
            This user does not have any transactions yet
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col mt-10">
      <div className="overflow-x-auto">
        <table className="w-full border border-[#FFFFFF1A] rounded-2xl overflow-hidden">
          <thead>
            <tr className="bg-gray-100 dark:bg-[#FFFFFF1A] text-gray-600 dark:text-white uppercase text-xs bp:text-sm leading-normal h-[50px]">
              <th className="hidden px-6 text-left sm:table-cell">Order#</th>
              <th className="px-6 text-left sm:hidden">Status</th>
              <th className="px-6 text-left">Items</th>
              <th className="px-6 text-left hidden sm:table-cell">
                Date & Time
              </th>
              <th className="px-6 text-left hidden sm:table-cell">Status</th>
              <th className="px-6 text-left">Amount</th>
            </tr>
          </thead>
          <tbody className="text-gray-600 dark:text-white text-xs bp:text-sm">
            {purchases.map((purchase) => (
              <tr
                key={purchase.id}
                className="border-b border-gray-200 h-[50px] hover:bg-gray-50 dark:hover:bg-gray-50/5 cursor-pointer"
                onClick={() => handleRowClick(purchase)}
              >
                <td className="hidden sm:table-cell px-6">{purchase.id}</td>
                <td className="sm:hidden px-6">
                  <div className="flex items-center gap-3">
                    <div
                      className={`h-2.5 w-2.5 rounded-full shrink-0 ${
                        statusBgStyles[getStatusText(purchase)] ||
                        "bg-orange-100"
                      }`}
                    />
                    <span>{purchase.id}</span>
                  </div>
                </td>
                <td className="px-6">{getItemsText(purchase)}</td>
                <td className="px-6 hidden sm:table-cell">
                  <div className="flex flex-col">
                    <span>{purchase.date || formatDate(purchase.created_at)}</span>
                    <span className="text-[11px] text-gray-500 dark:text-gray-400">
                      {purchase.time || formatTime(purchase.created_at)}
                    </span>
                  </div>
                </td>
                <td className="px-6 hidden sm:table-cell">
                  <StatusBadge statusText={getStatusText(purchase)} />
                </td>
                <td className="px-6 font-bold">{formatAmount(purchase)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <TransactionDetailsModal
        isOpen={isModalOpen}
        onClose={closeModal}
        transaction={selectedTransaction}
      />
    </div>
  );
};
